/**
 * BottomSheet - iOS-style bottom sheet modal
 * Apple-inspired design with native iOS feel
 *
 * Features:
 * - Safe area insets (top + bottom) via env()
 * - iOS momentum scrolling with overscroll containment
 * - Focus trap for accessibility
 * - Prevents background scroll bleed
 * - 44px minimum touch targets per Apple HIG
 * - Reduced motion support
 */

import { memo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from '@/lib/icons';
import { triggerHaptic } from '@/lib/haptics';
import { useReducedMotion, useVisualViewport, getPortalRoot } from '@/lib/hooks';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** Optional subtitle below the title */
  subtitle?: string;
  /** Optional icon element to show in header */
  headerIcon?: React.ReactNode;
  /** Background color class for the header icon container (e.g., 'bg-primary-50') */
  headerIconBg?: string;
  /** Custom header content - replaces default title/icon header when provided */
  customHeader?: React.ReactNode;
  children: React.ReactNode;
  maxHeight?: string;
  /** Whether content should be scrollable within sheet (default: true) */
  scrollable?: boolean;
  /** Whether to show close button in header (default: true when title is present) */
  showCloseButton?: boolean;
  /** ARIA label for the sheet (used when no title is provided) */
  ariaLabel?: string;
}

export const BottomSheet = memo(function BottomSheet({
  isOpen,
  onClose,
  title,
  subtitle,
  headerIcon,
  headerIconBg = 'bg-gray-100',
  customHeader,
  children,
  maxHeight = '70vh',
  scrollable = true,
  showCloseButton,
  ariaLabel,
}: BottomSheetProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const { keyboardHeight, isKeyboardVisible } = useVisualViewport();
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Default showCloseButton to true when title is present
  const shouldShowCloseButton = showCloseButton ?? !!title;

  // Focus management and focus trap
  useEffect(() => {
    if (!isOpen || !sheetRef.current) return;

    // Store previously focused element
    previousActiveElement.current = document.activeElement as HTMLElement;

    // Get all focusable elements
    const getFocusableElements = () => {
      if (!sheetRef.current) return [];
      return Array.from(
        sheetRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
    };

    // Focus first element (close button or first focusable)
    const focusTimer = setTimeout(() => {
      if (closeButtonRef.current) {
        closeButtonRef.current.focus();
      } else {
        const focusableElements = getFocusableElements();
        focusableElements[0]?.focus();
      }
    }, 100);

    // Focus trap handler
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const elements = getFocusableElements();
      if (elements.length === 0) return;

      const first = elements[0];
      const last = elements[elements.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus on close
      previousActiveElement.current?.focus?.();
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        triggerHaptic();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleBackdropClick = useCallback(() => {
    triggerHaptic();
    onClose();
  }, [onClose]);

  const handleCloseClick = useCallback(() => {
    triggerHaptic();
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  // Animation classes based on motion preference
  const backdropAnimation = prefersReducedMotion ? '' : 'animate-fade-in';
  const sheetAnimation = prefersReducedMotion ? '' : 'animate-slide-up-sheet';

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{
        // Prevent touch events bleeding through to background
        touchAction: 'none',
        // Ensure we respect safe areas
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      {/* Backdrop - prevents iOS scroll-through and background interaction */}
      <div
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm ${backdropAnimation}`}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Sheet container - Refined Apple-inspired glass surface */}
      <div
        ref={sheetRef}
        className={`relative w-full max-w-lg bg-white/[0.98] backdrop-blur-xl rounded-t-[28px]
                   shadow-[0_-0.5px_0_rgba(0,0,0,0.03),0_-2px_8px_rgba(0,0,0,0.04),0_-8px_24px_rgba(0,0,0,0.06),0_-16px_48px_rgba(0,0,0,0.06)]
                   border-t border-x border-white/40
                   ${sheetAnimation}`}
        style={{
          // Account for safe area in max height calculation
          // Use 100dvh for iOS Safari address bar awareness
          maxHeight: `min(${maxHeight}, calc(100dvh - env(safe-area-inset-top, 0px) - 20px))`,
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'bottom-sheet-title' : undefined}
        aria-label={!title ? (ariaLabel || 'Bottom sheet') : undefined}
        tabIndex={-1}
      >
        {/* Handle bar - Refined iOS-style indicator */}
        <div className="flex justify-center pt-3 pb-2" aria-hidden="true">
          <div className="w-9 h-[5px] bg-gray-300/50 rounded-full" />
        </div>

        {/* Custom Header */}
        {customHeader}

        {/* Default Header - renders if title is provided and customHeader is not */}
        {title && !customHeader && (
          <header className="flex items-center justify-between px-5 py-2.5 border-b border-gray-100/80">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {headerIcon && (
                <span className={`w-10 h-10 rounded-xl ${headerIconBg} flex items-center justify-center flex-shrink-0`}>
                  {headerIcon}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <h3 id="bottom-sheet-title" className="text-body-lg font-semibold text-gray-900 leading-tight">{title}</h3>
                {subtitle && <p className="text-label text-gray-500 leading-snug mt-0.5 truncate">{subtitle}</p>}
              </div>
            </div>
            {shouldShowCloseButton && (
              <button
                ref={closeButtonRef}
                onClick={handleCloseClick}
                className="w-11 h-11 -mr-1.5 flex items-center justify-center flex-shrink-0
                          rounded-full hover:bg-gray-100/80 motion-safe:transition-colors touch-manipulation
                          active:scale-95 focus-visible:outline-none focus-visible:ring-2
                          focus-visible:ring-primary-200 focus-visible:ring-offset-2"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            )}
          </header>
        )}

        {/* Scrollable content area with iOS momentum scrolling */}
        <div
          className={`px-5 pt-3 motion-safe:transition-[padding-bottom] motion-safe:duration-150 ${scrollable ? 'overflow-y-auto overscroll-contain' : ''}`}
          style={{
            ...(scrollable ? {
              maxHeight: `calc(${maxHeight} - 120px)`,
              WebkitOverflowScrolling: 'touch',
              // Prevent scroll chaining to background
              overscrollBehavior: 'contain',
            } : {}),
            // Bottom padding includes safe area AND keyboard height when visible
            paddingBottom: isKeyboardVisible
              ? `calc(${keyboardHeight}px + 12px)`
              : 'max(16px, env(safe-area-inset-bottom, 16px))',
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    getPortalRoot()
  );
});

